import { memo, useEffect, useRef } from "react";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { RoleName } from "consts/auth";
import Profile from "ui/layouts/Profile";
import { useUnsavedChangesContext } from "ui/widgets/UnsavedChanges";
import { RoleBasedTabs } from "ui/widgets/StatefulTabs";
import useMerchantDetail from "./useMerchantDetail";
import MerchantDetailTab from "./MerchantDetailTab";
import MerchantProfile from "./MerchantProfile";
import useMerchantDetailRepository from "./useMerchantDetailRepository";
import MerchantUsers from "./MerchantUsers";
import MerchantAccountSettings from "./MerchantAccountSettings";
import useResetMerchantDetail from "./useResetMerchantDetail";
import usePersistMerchant from "../usePersistMerchant";
import MerchantPaymentMethods from "./MerchantPaymentMethods";
import MerchantDomains from "./MerchantDomains";
import MerchantFeesAndCommisions from "./MerchantFeesAndCommisions";
import MerchantPlayground from "./MerchantPlayground";
import { MerchantDetailTab as MerchantDetailTabEnum } from "./types";

const MerchantDetail = () => {
    const { checkUnsavedChanges } = useUnsavedChangesContext();

    const {
        persistMerchantActiveTab,
        getMerchantActiveTab
    } = usePersistMerchant();

    const merchantDetail = useMerchantDetail({
        onBeforeClose: () => checkUnsavedChanges({
            onCancel: () => Promise.reject()
        })
    });

    const {
        merchant,
        onClose,
    } = merchantDetail;

    const repository = useMerchantDetailRepository();
    const paymentMethodsRepository = repository.get(MerchantDetailTabEnum.PaymentMethods)!;
    const paymentMethodsRepositoryRef = useRef(paymentMethodsRepository);
    paymentMethodsRepositoryRef.current = paymentMethodsRepository;

    useEffect(() => {
        paymentMethodsRepositoryRef.current.fetchMerchantsMethods();
    }, [merchant.coreId]);

    const handleTabChange = (tab: string) =>
        checkUnsavedChanges({
            onConfirm: () => persistMerchantActiveTab(tab),
            onCancel: () => Promise.reject()
        });

    useResetMerchantDetail();

    return (
        <Paper
            sx={{
                position: 'absolute',
                inset: 0,
                transform: 'translateX(100%)',
                animation: 'slideLeft .3s ease-in-out forwards',
                height: '100%',
                p: 2
            }}
        >
            <Profile
                backButtonSlot={(
                    <IconButton
                        onClick={onClose}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                )}
                AccountProps={{
                    children: (
                        <>
                            <Typography
                                variant='body2'
                            >
                                #{merchant.coreId}
                            </Typography>
                            <Typography
                                variant='h6'
                            >
                                {merchant.fullName}
                            </Typography>
                        </>
                    ),
                    CustomAvatarProps: {
                        name: merchant.fullName
                    }
                }}
            >
                <RoleBasedTabs
                    model={[
                        {
                            label: 'Profile',
                            component: (
                                <MerchantDetailTab
                                    repository={repository.get(MerchantDetailTabEnum.Profile)!}
                                >
                                    <MerchantProfile
                                        {...merchantDetail}
                                    />
                                </MerchantDetailTab>
                            ),
                            roles: [
                                RoleName.Admin,
                                RoleName.TechAdmin,
                                RoleName.FinanceAdmin,
                                RoleName.ComplianceAdmin,
                                RoleName.OpsSalesAdmin
                            ]
                        },
                        {
                            label: 'Users',
                            component: (
                                <MerchantDetailTab
                                    repository={repository.get(MerchantDetailTabEnum.Users)!}
                                >
                                    <MerchantUsers />
                                </MerchantDetailTab>
                            ),
                            sxProps: {
                                padding: 0
                            },
                            roles: [
                                RoleName.Admin,
                                RoleName.TechAdmin,
                                RoleName.FinanceAdmin,
                                RoleName.ComplianceAdmin,
                                RoleName.OpsSalesAdmin
                            ]
                        },
                        {
                            label: 'Account Settings',
                            component: (
                                <MerchantDetailTab
                                    repository={repository.get(MerchantDetailTabEnum.AccountSettings)!}
                                >
                                    <MerchantAccountSettings
                                        {...merchantDetail}
                                    />
                                </MerchantDetailTab>
                            ),
                            sxProps: {
                                padding: 0
                            },
                            roles: [
                                RoleName.Admin,
                                RoleName.TechAdmin,
                                RoleName.FinanceAdmin,
                                RoleName.ComplianceAdmin,
                                RoleName.OpsSalesAdmin
                            ]
                        },
                        {
                            label: 'Payment Methods',
                            component: (
                                <MerchantDetailTab
                                    repository={repository.get(MerchantDetailTabEnum.PaymentMethods)!}
                                >
                                    <MerchantPaymentMethods />
                                </MerchantDetailTab>
                            ),
                            roles: [
                                RoleName.Admin,
                                RoleName.TechAdmin,
                                RoleName.FinanceAdmin,
                                RoleName.ComplianceAdmin,
                                RoleName.OpsSalesAdmin
                            ]
                        },
                        {
                            label: 'Playground',
                            component: (
                                <MerchantPlayground
                                    merchant={merchant}
                                    paymentMethodsRepository={paymentMethodsRepository}
                                />
                            ),
                            roles: [
                                RoleName.Admin
                            ]
                        },
                        {
                            label: 'Fees & Commissions',
                            component: (
                                <MerchantDetailTab
                                    repository={repository.get(MerchantDetailTabEnum.FeeAndCommissions)!}
                                >
                                    <MerchantFeesAndCommisions />
                                </MerchantDetailTab>
                            ),
                            roles: [
                                RoleName.Admin,
                                RoleName.TechAdmin,
                                RoleName.FinanceAdmin,
                                RoleName.ComplianceAdmin,
                                RoleName.OpsSalesAdmin
                            ]
                        },
                        {
                            label: 'Domains',
                            component: (
                                <MerchantDetailTab
                                    repository={repository.get(MerchantDetailTabEnum.Domains)!}
                                >
                                    <MerchantDomains />
                                </MerchantDetailTab>
                            ),
                            roles: [
                                RoleName.Admin,
                                RoleName.TechAdmin,
                                RoleName.FinanceAdmin,
                                RoleName.ComplianceAdmin,
                                RoleName.OpsSalesAdmin,
                                RoleName.Employee
                            ]
                        }
                    ]}
                    TabListContainerProps={{
                        borderBottom: 1,
                        borderColor: 'divider'
                    }}
                    TabListProps={{
                        'aria-label': 'merchant details',
                        variant: 'scrollable',
                        scrollButtons: 'auto'
                    }}
                    value={getMerchantActiveTab()}
                    onChange={handleTabChange}
                />
            </Profile>
        </Paper>
    );
};

export default memo(MerchantDetail);
